import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { 
  DollarSign, Shield, Users, Calendar, TrendingUp, 
  CheckCircle, Star, Clock, Zap, Award, Globe,
  ArrowRight, CreditCard, FileText, MessageSquare,
  Camera, Music, ChefHat, Sparkles
} from "lucide-react";

const RAFHA_LOGO = "https://customer-assets.emergentagent.com/job_4c248649-ca09-4085-a81a-27c2f8b8f7bc/artifacts/rogqq55c_F1371478-432E-404F-80CC-D2C1450E0839.png";

const VendorBenefits = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const benefits = [
    {
      icon: Users,
      title: "Access to Premium Clients",
      description: "Connect with clients actively searching for quality event professionals. No more cold calling or expensive advertising."
    },
    {
      icon: Shield,
      title: "Vetted & Verified Badge",
      description: "Stand out with our verification badge that builds instant trust. Clients prefer verified vendors over unverified competitors."
    },
    {
      icon: Calendar,
      title: "Smart Booking System",
      description: "Manage your availability with our calendar. Block dates, track bookings, and never double-book again."
    },
    {
      icon: FileText,
      title: "Professional Contracts",
      description: "We provide legally-sound contracts with digital signatures. Protect yourself and your business professionally."
    },
    {
      icon: MessageSquare,
      title: "Built-in Messaging",
      description: "Communicate directly with clients through our secure platform. Keep all conversations organized in one place."
    },
    {
      icon: TrendingUp,
      title: "Grow Your Reputation",
      description: "Collect reviews from satisfied clients. Build a portfolio that showcases your best work and attracts more bookings."
    }
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Apply & Get Verified",
      description: "Submit your application with business info, portfolio, and credentials. Our team reviews within 2-3 business days."
    },
    {
      step: "02",
      title: "Set Your Profile",
      description: "Create your vendor profile with photos, pricing, services offered, and availability calendar."
    },
    {
      step: "03",
      title: "Receive Booking Requests",
      description: "Clients browse the marketplace and send booking inquiries. You review and accept the ones that fit your schedule."
    },
    {
      step: "04",
      title: "Confirm & Get Paid",
      description: "Once confirmed, clients sign the contract and pay the service fee. You collect your payment directly at the event."
    }
  ];

  const feeStructure = {
    serviceFee: "10%",
    minimumFee: "$25",
    description: "We only charge a small service fee when you get booked. No monthly fees, no hidden costs."
  };

  const faqs = [
    {
      question: "How much does it cost to join?",
      answer: "Joining Rafha Entertainment is completely FREE. There are no signup fees, monthly fees, or hidden costs. We only charge a small service fee (10%, minimum $25) when you successfully book an event through our platform."
    },
    {
      question: "How do I get paid?",
      answer: "You get paid directly by the client! When a booking is confirmed, the client pays a service fee to Rafha Entertainment. The remaining balance (your full rate minus service fee) is paid directly to you by the client at the event. This means faster payments with no waiting for platform payouts."
    },
    {
      question: "What's the service fee for?",
      answer: "The service fee covers: platform maintenance, marketing to bring you clients, secure contract management, our verification system, customer support, and payment processing. It's only charged when you actually book work through us."
    },
    {
      question: "How long does verification take?",
      answer: "Most applications are reviewed within 2-3 business days. Make sure to provide complete information, valid business credentials, and a quality portfolio to speed up the process."
    },
    {
      question: "Can I set my own prices?",
      answer: "Absolutely! You have complete control over your pricing. Set your hourly rate, package prices, or custom quotes. We never dictate what you charge for your services."
    },
    {
      question: "What if a client cancels?",
      answer: "Our contracts include cancellation policies that protect both parties. Depending on how close to the event the cancellation occurs, you may be entitled to a cancellation fee as outlined in the contract."
    }
  ];

  const vendorTypes = [
    { icon: Music, name: "DJs & Musicians" },
    { icon: Camera, name: "Photographers" },
    { icon: ChefHat, name: "Caterers" },
    { icon: Sparkles, name: "Decorators" },
    { icon: Star, name: "Entertainment" },
    { icon: Award, name: "Event Planners" }
  ];

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gold/5 to-transparent"></div>
        <div className="container mx-auto max-w-6xl relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-2 bg-gold/10 border border-gold/20 rounded-full text-gold text-sm font-medium mb-6">
              Join 100+ Event Professionals
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Grow Your Event Business with{" "}
              <span className="text-gold">Rafha Entertainment</span>
            </h1>
            <p className="text-white/60 text-lg md:text-xl max-w-3xl mx-auto mb-8">
              Get discovered by premium clients, manage bookings effortlessly, and focus on what you do best — 
              creating unforgettable events.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate("/auth")}
                className="bg-gold text-black hover:bg-gold-light rounded-full px-8 py-6 text-lg font-bold"
                data-testid="apply-now-hero"
              >
                Apply Now — It's Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                variant="outline"
                onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                className="border-white/20 text-white hover:bg-white/5 rounded-full px-8 py-6 text-lg"
              >
                See How It Works
              </Button>
            </div>
          </motion.div>

          {/* Vendor Types */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-16 flex flex-wrap justify-center gap-4"
          >
            {vendorTypes.map((type, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-4 py-2 bg-charcoal rounded-full border border-white/5"
              >
                <type.icon className="w-4 h-4 text-gold" />
                <span className="text-white/80 text-sm">{type.name}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Why Vendors Love Rafha
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Everything you need to manage and grow your event business in one platform.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-charcoal rounded-2xl border border-white/5 p-6 hover:border-gold/20 transition-colors"
              >
                <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-gold" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-2">
                  {benefit.title}
                </h3>
                <p className="text-white/60">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fee Structure Section */}
      <section className="py-16 px-6 bg-charcoal/50">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full text-green-400 text-sm font-medium mb-6">
              <CheckCircle className="w-4 h-4" />
              Transparent Pricing
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Simple, Fair Pricing
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto mb-8">
              No monthly fees. No signup costs. You only pay when you book work.
            </p>

            <div className="bg-obsidian rounded-2xl border border-gold/20 p-8 max-w-xl mx-auto">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="text-center">
                  <p className="text-5xl font-bold text-gold">{feeStructure.serviceFee}</p>
                  <p className="text-white/60 text-sm">Service Fee</p>
                </div>
                <div className="text-white/40 text-2xl">|</div>
                <div className="text-center">
                  <p className="text-5xl font-bold text-gold">{feeStructure.minimumFee}</p>
                  <p className="text-white/60 text-sm">Minimum Fee</p>
                </div>
              </div>
              
              <div className="space-y-3 text-left">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-white/80">Free to join and create your profile</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-white/80">Only charged when you get booked</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-white/80">Client pays service fee at booking</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-white/80">You collect your full rate directly from client</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-white/50 text-sm">
                  <strong className="text-white">Example:</strong> If your rate is $500, the client pays $50 service fee to us, 
                  and pays you $500 directly at the event. You keep 100% of your rate!
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              From application to your first booking in 4 simple steps.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-charcoal rounded-2xl border border-white/5 p-6 h-full">
                  <span className="text-6xl font-bold text-gold/20">{step.step}</span>
                  <h3 className="font-heading text-xl font-bold text-white mt-2 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-white/60 text-sm">
                    {step.description}
                  </p>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-gold/30" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 bg-charcoal/50">
        <div className="container mx-auto max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-obsidian rounded-xl border border-white/5 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                >
                  <span className="font-medium text-white">{faq.question}</span>
                  <ArrowRight 
                    className={`w-5 h-5 text-gold transition-transform ${
                      openFaq === index ? "rotate-90" : ""
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-white/60">{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-gold/20 to-gold/5 rounded-3xl border border-gold/30 p-8 md:p-12 text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Grow Your Business?
            </h2>
            <p className="text-white/70 max-w-xl mx-auto mb-8">
              Join Rafha Entertainment today and start connecting with premium clients 
              who are looking for exactly what you offer.
            </p>
            <Button
              onClick={() => navigate("/auth")}
              className="bg-gold text-black hover:bg-gold-light rounded-full px-10 py-6 text-lg font-bold"
              data-testid="apply-now-cta"
            >
              Apply Now — It's Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <p className="text-white/40 text-sm mt-4">
              No credit card required • Free to join • Apply in minutes
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VendorBenefits;
